/*
 * Street Garms — SG-041 Accessible Component Standard
 * Progressive enhancement for Kalles 4.1.5 interactive components.
 */
(() => {
  'use strict';

  if (window.sgAccessibilityStandardLoaded) return;
  window.sgAccessibilityStandardLoaded = true;

  /*
   * Input-modality guard for focus styling. iOS Safari can keep :focus-visible
   * on touch-focused elements with tabindex, which made whole galleries and
   * sections appear with Street Garms green boxes. Only enable the strong
   * focus treatment after real keyboard navigation; remove it on touch/mouse.
   */
  const setKeyboardNavigation = (event) => {
    if (event.key === 'Tab') document.documentElement.classList.add('sg-keyboard-nav');
  };
  const setPointerNavigation = () => document.documentElement.classList.remove('sg-keyboard-nav');

  document.addEventListener('keydown', setKeyboardNavigation, true);
  document.addEventListener('pointerdown', setPointerNavigation, true);
  document.addEventListener('touchstart', setPointerNavigation, { capture: true, passive: true });

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const DIALOG_SELECTOR = [
    '.t4s-drawer[role="dialog"]',
    '.t4s-modal[role="dialog"]',
    '.pswp[role="dialog"]',
    '.bnpl-modal-overlay[role="dialog"]',
    '.sg-popup-blur-overlay[role="dialog"]'
  ].join(',');

  const drawerLabels = {
    't4s-menu-drawer': 'Main menu',
    't4s-mini_cart': 'Shopping cart',
    't4s-login-sidebar': 'Account sign in',
    't4s-search-hidden': 'Search',
    't4s-filter-hidden': 'Filter products'
  };

  const openerByDialog = new WeakMap();
  let uid = 0;

  const nextId = (prefix = 'sg-a11y') => `${prefix}-${++uid}`;
  const cssEscape = (value) => window.CSS && typeof window.CSS.escape === 'function'
    ? window.CSS.escape(String(value))
    : String(value).replace(/[^a-zA-Z0-9_-]/g, (character) => `\\${character}`);

  const isVisible = (element) => {
    if (!element || !document.documentElement.contains(element)) return false;
    if (element.hasAttribute('hidden') || element.closest('[hidden]')) return false;
    // Ignore an element's own inert state here. Kalles opens drawers by first
    // switching aria-hidden to false; our observer must still be able to detect
    // that transition and remove inert from the drawer itself. Descendants of
    // an inert ancestor remain correctly treated as unavailable.
    if (element.parentElement && element.parentElement.closest('[inert]')) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };

  const isDialogOpen = (dialog) => {
    if (!dialog || !isVisible(dialog)) return false;
    const hidden = dialog.getAttribute('aria-hidden');
    if (hidden === 'true') return false;
    if (hidden === 'false') return true;
    return dialog.classList.contains('is--opened') ||
      dialog.classList.contains('is-open') ||
      dialog.classList.contains('is-active') ||
      dialog.classList.contains('pswp--open');
  };

  const focusables = (container) => Array.from(container.querySelectorAll(FOCUSABLE))
    .filter((element) => isVisible(element) && element.getAttribute('aria-hidden') !== 'true');

  const focusFirst = (dialog) => {
    if (!dialog || dialog.contains(document.activeElement)) return;
    const preferred = dialog.querySelector('[data-opend-focus], input[type="search"], input[type="email"], [data-drawer-close], [data-t4s-modal-close], .pswp__button--close, .bnpl-modal-close, .sg-popup-close-btn');
    if (preferred && isVisible(preferred)) {
      preferred.focus({ preventScroll: true });
      return;
    }
    const first = focusables(dialog)[0];
    if (first) first.focus({ preventScroll: true });
    else {
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
    }
  };

  const restoreOpener = (dialog) => {
    const opener = openerByDialog.get(dialog);
    if (opener && document.documentElement.contains(opener) && isVisible(opener)) {
      window.setTimeout(() => opener.focus({ preventScroll: true }), 40);
    }
  };

  const syncTrigger = (trigger) => {
    const id = trigger.getAttribute('aria-controls');
    if (!id) return;
    const controlled = document.getElementById(id);
    if (!controlled) return;
    trigger.setAttribute('aria-expanded', isDialogOpen(controlled) ? 'true' : 'false');
  };

  const enhanceDialog = (dialog) => {
    if (!dialog) return;

    if (!dialog.hasAttribute('role')) dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

    if (!dialog.hasAttribute('aria-label') && !dialog.hasAttribute('aria-labelledby')) {
      const label = drawerLabels[dialog.id] || 'Dialog';
      dialog.setAttribute('aria-label', label);
    }

    const open = isDialogOpen(dialog);
    if (open) {
      dialog.removeAttribute('inert');
      window.setTimeout(() => focusFirst(dialog), 80);
    } else {
      dialog.setAttribute('inert', '');
    }

    if (dialog.id) {
      document.querySelectorAll(`[aria-controls="${cssEscape(dialog.id)}"]`).forEach(syncTrigger);
    }
  };

  const enhanceDrawers = (root = document) => {
    root.querySelectorAll?.('.t4s-drawer').forEach((drawer) => {
      if (!drawer.hasAttribute('role')) drawer.setAttribute('role', 'dialog');
      if (!drawer.hasAttribute('aria-modal')) drawer.setAttribute('aria-modal', 'true');
      if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
      if (!drawer.hasAttribute('aria-label') && !drawer.hasAttribute('aria-labelledby')) {
        drawer.setAttribute('aria-label', drawerLabels[drawer.id] || 'Panel');
      }
      enhanceDialog(drawer);
    });
  };

  const enhanceNotices = (root = document) => {
    root.querySelectorAll?.('#t4s-notices__wrapper').forEach((notice) => {
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.setAttribute('aria-atomic', 'true');
    });
    root.querySelectorAll?.('.t4s-notices__close').forEach((button) => {
      if (!button.hasAttribute('aria-label')) button.setAttribute('aria-label', 'Dismiss notification');
      if (button.tagName === 'BUTTON' && !button.hasAttribute('type')) button.setAttribute('type', 'button');
    });
  };

  const enhanceQuantity = (root = document) => {
    root.querySelectorAll?.('[data-decrease-qty]').forEach((button) => {
      if (!button.hasAttribute('aria-label')) button.setAttribute('aria-label', 'Decrease quantity');
    });
    root.querySelectorAll?.('[data-increase-qty]').forEach((button) => {
      if (!button.hasAttribute('aria-label')) button.setAttribute('aria-label', 'Increase quantity');
    });
    root.querySelectorAll?.('[data-quantity-value]').forEach((input) => {
      if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) input.setAttribute('aria-label', 'Quantity');
    });
  };

  const setAccordionState = (trigger, panel, expanded) => {
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.setAttribute('role', 'region');
    if (!trigger.id) trigger.id = nextId('sg-accordion-trigger');
    panel.setAttribute('aria-labelledby', trigger.id);
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
  };

  const enhanceAccordionsAndTabs = (root = document) => {
    root.querySelectorAll?.('.t4s-type-accordion [data-t4s-tab-wrapper]').forEach((wrapper) => {
      const trigger = wrapper.querySelector(':scope > [data-t4s-tab-item], :scope > .t4s-accor-title');
      const panel = wrapper.querySelector(':scope > [data-t4s-tab-content]');
      if (!trigger || !panel) return;
      if (!panel.id) panel.id = nextId('sg-accordion-panel');
      trigger.setAttribute('role', 'button');
      setAccordionState(trigger, panel, wrapper.classList.contains('t4s-active'));
    });

    root.querySelectorAll?.('.t4s-tabs-ul').forEach((tablist) => {
      tablist.setAttribute('role', 'tablist');
      tablist.querySelectorAll('[data-t4s-tab-item]').forEach((tab) => {
        const selector = tab.getAttribute('href') || tab.getAttribute('data-id-tab');
        if (!selector || selector.charAt(0) !== '#') return;
        const panel = document.getElementById(selector.slice(1));
        if (!panel) return;
        if (!tab.id) tab.id = nextId('sg-tab');
        const selected = tab.classList.contains('t4s-active');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });
    });
  };

  const activateMobileTab = (tab) => {
    if (!tab) return;
    tab.click();
    window.setTimeout(() => enhanceMobileNavTabs(document), 30);
  };

  const enhanceMobileNavTabs = (root = document) => {
    root.querySelectorAll?.('[data-tab-mb-nav]').forEach((tablist) => {
      tablist.setAttribute('role', 'tablist');
      const tabs = Array.from(tablist.querySelectorAll('[data-tab-mb-item]'));
      tabs.forEach((tab, index) => {
        const selector = tab.getAttribute('data-id');
        if (!selector || selector.charAt(0) !== '#') return;
        const panel = document.getElementById(selector.slice(1));
        if (!panel) return;
        if (!tab.id) tab.id = nextId('sg-mobile-menu-tab');
        const active = tab.classList.contains('is--active');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('tabindex', active ? '0' : '-1');
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        tab.dataset.sgTabIndex = String(index);
      });
    });
  };

  const enhanceMobileSubmenus = (root = document) => {
    root.querySelectorAll?.('#t4s-menu-drawer .t4s-menu-item-has-children').forEach((item) => {
      const trigger = item.querySelector(':scope > a');
      const submenu = item.querySelector(':scope > ul');
      if (!trigger || !submenu) return;
      if (!submenu.id) submenu.id = nextId('sg-mobile-submenu');
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-controls', submenu.id);
      trigger.setAttribute('aria-expanded', item.classList.contains('is--opend') ? 'true' : 'false');
      submenu.setAttribute('aria-hidden', item.classList.contains('is--opend') ? 'false' : 'true');
    });
  };

  const enhanceDesktopNavigation = (root = document) => {
    root.querySelectorAll?.('.t4s-navigation .t4s-menu-item.has--children').forEach((item) => {
      const trigger = item.querySelector(':scope > a');
      const submenu = item.querySelector(':scope > .t4s-sub-menu');
      if (!trigger || !submenu) return;
      if (!submenu.id) submenu.id = nextId('sg-submenu');
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-controls', submenu.id);
      const expanded = item.matches(':hover') || item.contains(document.activeElement) || item.classList.contains('is-action__hover');
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  const enhanceDropdowns = (root = document) => {
    root.querySelectorAll?.('[data-dropdown-open][data-id]').forEach((trigger) => {
      const id = trigger.getAttribute('data-id');
      if (!id) return;
      trigger.setAttribute('aria-controls', id);
      if (!trigger.hasAttribute('aria-haspopup')) trigger.setAttribute('aria-haspopup', 'listbox');
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      if (trigger.tagName === 'BUTTON' && !trigger.hasAttribute('type')) trigger.setAttribute('type', 'button');
      const popup = document.getElementById(id);
      if (popup) {
        const optionContainer = popup.querySelector('.t4s-dropdown__content') || popup;
        optionContainer.setAttribute('role', 'listbox');
        if (!optionContainer.hasAttribute('aria-label')) optionContainer.setAttribute('aria-label', 'Options');
        popup.querySelectorAll('[data-dropdown-item], [data-sortby-item]').forEach((option) => {
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', option.classList.contains('is--selected') ? 'true' : 'false');
          if (option.tagName === 'BUTTON' && !option.hasAttribute('type')) option.setAttribute('type', 'button');
        });
      }
    });
  };

  const enhanceCarousels = (root = document) => {
    root.querySelectorAll?.('[data-flickityt4s-js], .flickityt4s').forEach((carousel) => {
      if (!carousel.hasAttribute('role')) carousel.setAttribute('role', 'region');
      if (!carousel.hasAttribute('aria-roledescription')) carousel.setAttribute('aria-roledescription', 'carousel');
      if (!carousel.hasAttribute('aria-label') && !carousel.hasAttribute('aria-labelledby')) {
        const section = carousel.closest('section, .shopify-section, .t4s-section');
        const heading = section?.querySelector('h1, h2, h3');
        if (heading) {
          if (!heading.id) heading.id = nextId('sg-carousel-heading');
          carousel.setAttribute('aria-labelledby', heading.id);
        } else {
          carousel.setAttribute('aria-label', 'Carousel');
        }
      }
      carousel.querySelectorAll('.flickityt4s-slider > *, .flickity-slider > *').forEach((slide, index, slides) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`);
      });
      carousel.querySelectorAll('.flickityt4s-page-dots button, .flickity-page-dots button, .dot').forEach((dot, index) => {
        if (dot.tagName === 'BUTTON' && !dot.hasAttribute('type')) dot.setAttribute('type', 'button');
        if (!dot.hasAttribute('aria-label')) dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      });
    });
  };

  const reduceMotion = () => {
    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('[data-flickityt4s-js]').forEach((carousel) => {
      const raw = carousel.getAttribute('data-flickityt4s-js');
      if (raw) {
        try {
          const config = JSON.parse(raw);
          if (config.autoPlay) {
            config.autoPlay = 0;
            carousel.setAttribute('data-flickityt4s-js', JSON.stringify(config));
          }
        } catch (_) {}
      }
      try {
        const FlickityCtor = window.Flickity || window.flickityt4s;
        const instance = FlickityCtor?.data ? FlickityCtor.data(carousel) : null;
        instance?.stopPlayer?.();
        instance?.pausePlayer?.();
      } catch (_) {}
    });
  };


  const enhanceLegacyButtons = (root = document) => {
    root.querySelectorAll?.('[data-cart-tool_action], [data-toogle-gift], .ssw-dropdown-toggle[href^="javascript:"], #customer_login_link[href^="javascript:"], #customer_register_link[href^="javascript:"]').forEach((control) => {
      if (control.tagName === 'BUTTON' || control.tagName === 'A') return;
      control.setAttribute('role', 'button');
      if (!control.hasAttribute('tabindex')) control.setAttribute('tabindex', '0');
      if (!control.hasAttribute('aria-label')) {
        const label = control.getAttribute('title') || control.textContent.trim();
        if (label) control.setAttribute('aria-label', label);
      }
    });
  };

  const enhanceBasicControls = (root = document) => {
    root.querySelectorAll?.('.t4s-search-form__clear-action').forEach((button) => {
      if (!button.hasAttribute('aria-label')) button.setAttribute('aria-label', 'Clear search');
    });
    root.querySelectorAll?.('[data-drawer-close], [data-t4s-modal-close], .t4s-modal-close').forEach((button) => {
      if (button.tagName === 'BUTTON' && !button.hasAttribute('type')) button.setAttribute('type', 'button');
    });
    root.querySelectorAll?.('[data-layout-switch] [data-btn-as-a]').forEach((button) => {
      if (button.tagName === 'BUTTON' && !button.hasAttribute('type')) button.setAttribute('type', 'button');
      button.setAttribute('aria-pressed', button.classList.contains('is--active') ? 'true' : 'false');
    });
    root.querySelectorAll?.('a[target="_blank"]').forEach((link) => {
      const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
      rel.add('noopener');
      link.setAttribute('rel', Array.from(rel).join(' '));
    });
  };

  const enhance = (root = document) => {
    enhanceDrawers(root);
    root.querySelectorAll?.(DIALOG_SELECTOR).forEach(enhanceDialog);
    enhanceNotices(root);
    enhanceQuantity(root);
    enhanceAccordionsAndTabs(root);
    enhanceMobileNavTabs(root);
    enhanceMobileSubmenus(root);
    enhanceDesktopNavigation(root);
    enhanceDropdowns(root);
    enhanceCarousels(root);
    enhanceLegacyButtons(root);
    enhanceBasicControls(root);
    reduceMotion();
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest('[aria-controls]');
    if (control) {
      const id = control.getAttribute('aria-controls');
      const dialog = id ? document.getElementById(id) : null;
      if (dialog?.matches(DIALOG_SELECTOR) || dialog?.classList.contains('t4s-drawer')) {
        openerByDialog.set(dialog, control);
        window.setTimeout(() => enhanceDialog(dialog), 50);
      }
    }

    const mobileSubmenuItem = event.target.closest('#t4s-menu-drawer .t4s-menu-item-has-children');
    if (mobileSubmenuItem) window.setTimeout(() => enhanceMobileSubmenus(document), 30);

    const navItem = event.target.closest('.t4s-navigation .t4s-menu-item.has--children');
    if (navItem) window.setTimeout(() => enhanceDesktopNavigation(document), 20);

    const accordionTrigger = event.target.closest('.t4s-type-accordion [data-t4s-tab-item]');
    if (accordionTrigger) window.setTimeout(() => enhanceAccordionsAndTabs(document), 30);

    const tabTrigger = event.target.closest('.t4s-tabs-ul [data-t4s-tab-item]');
    if (tabTrigger) window.setTimeout(() => enhanceAccordionsAndTabs(document), 30);

    const dropdownTrigger = event.target.closest('[data-dropdown-open][data-id]');
    if (dropdownTrigger) {
      dropdownTrigger.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => enhanceDropdowns(document), 30);
    }

    const dropdownItem = event.target.closest('[data-dropdown-item], [data-sortby-item]');
    if (dropdownItem) {
      const popup = dropdownItem.closest('[data-dropdown-wrapper]');
      const id = popup?.id;
      if (id) window.setTimeout(() => document.querySelectorAll(`[data-dropdown-open][data-id="${cssEscape(id)}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false')), 30);
    }

    const layoutButton = event.target.closest('[data-layout-switch] [data-btn-as-a]');
    if (layoutButton) window.setTimeout(() => enhanceBasicControls(document), 30);

    const dropdownClose = event.target.closest('[data-dropdown-close]');
    if (dropdownClose) {
      const popup = dropdownClose.closest('[role="listbox"], [id]');
      const id = popup?.id;
      if (id) document.querySelectorAll(`[data-dropdown-open][data-id="${cssEscape(id)}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    const item = event.target.closest('.t4s-navigation .t4s-menu-item.has--children');
    if (item) enhanceDesktopNavigation(document);
  });

  document.addEventListener('focusout', () => {
    window.setTimeout(() => enhanceDesktopNavigation(document), 0);
  });

  document.addEventListener('keydown', (event) => {
    const legacyButton = event.target.closest('[data-cart-tool_action][role="button"], [data-toogle-gift][role="button"], .ssw-dropdown-toggle[role="button"], #customer_login_link[role="button"], #customer_register_link[role="button"]');
    if (legacyButton && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      legacyButton.click();
      return;
    }

    const accordionButton = event.target.closest('.t4s-type-accordion [data-t4s-tab-item][role="button"]');
    if (accordionButton && event.key === ' ') {
      event.preventDefault();
      accordionButton.click();
      return;
    }

    const mobileSubmenuTrigger = event.target.closest('#t4s-menu-drawer .t4s-menu-item-has-children > a');
    if (mobileSubmenuTrigger && event.key === ' ') {
      event.preventDefault();
      mobileSubmenuTrigger.click();
      return;
    }

    const mobileTab = event.target.closest('[data-tab-mb-item][role="tab"]');
    if (mobileTab) {
      const tablist = mobileTab.closest('[role="tablist"]');
      const tabs = Array.from(tablist?.querySelectorAll('[data-tab-mb-item][role="tab"]') || []);
      const index = tabs.indexOf(mobileTab);
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        activateMobileTab(mobileTab);
        return;
      }
      let next = null;
      if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
      if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
      if (event.key === 'Home') next = tabs[0];
      if (event.key === 'End') next = tabs[tabs.length - 1];
      if (next) {
        event.preventDefault();
        next.focus();
        activateMobileTab(next);
        return;
      }
    }

    const desktopTab = event.target.closest('.t4s-tabs-ul [role="tab"]');
    if (desktopTab && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      const list = desktopTab.closest('[role="tablist"]');
      const tabs = Array.from(list?.querySelectorAll('[role="tab"]') || []);
      const index = tabs.indexOf(desktopTab);
      let next = null;
      if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
      if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
      if (event.key === 'Home') next = tabs[0];
      if (event.key === 'End') next = tabs[tabs.length - 1];
      if (next) {
        event.preventDefault();
        next.focus();
        next.click();
        return;
      }
    }

    if (event.key !== 'Tab' && event.key !== 'Escape') return;

    const openDialogs = Array.from(document.querySelectorAll(DIALOG_SELECTOR)).filter(isDialogOpen);
    const dialog = openDialogs[openDialogs.length - 1];
    if (!dialog) return;

    if (event.key === 'Escape') {
      const close = dialog.querySelector('[data-drawer-close], [data-t4s-modal-close], .t4s-modal-close, .pswp__button--close, .bnpl-modal-close, .sg-popup-close-btn');
      if (close) {
        event.preventDefault();
        close.click();
      }
      return;
    }

    const items = focusables(dialog);
    if (!items.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  const observer = new MutationObserver((mutations) => {
    let needsEnhance = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length) needsEnhance = true;
      if (mutation.type === 'attributes') {
        const target = mutation.target;
        if (target.matches?.(DIALOG_SELECTOR) || target.classList?.contains('t4s-drawer')) {
          const wasInert = target.hasAttribute('inert');
          const open = isDialogOpen(target);
          if (open && wasInert) target.removeAttribute('inert');
          if (!open && !wasInert) target.setAttribute('inert', '');
          if (!open) restoreOpener(target);
          enhanceDialog(target);
        }
        needsEnhance = true;
      }
    });
    if (needsEnhance) window.requestAnimationFrame(() => enhance(document));
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'aria-hidden', 'style']
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhance(document), { once: true });
  } else {
    enhance(document);
  }

  document.addEventListener('shopify:section:load', (event) => enhance(event.target || document));
})();
